
import React, { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import LoginForm from '../../components/admin/LoginForm';
import Dashboard from '../../components/admin/Dashboard';

interface Article {
  id: number;
  category: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  readTime: string;
  image: string;
  tags: string[];
  status: 'published' | 'draft';
}

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'articles'>('dashboard');
  const [articles, setArticles] = useState<Article[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    excerpt: '',
    content: '',
    tags: '',
    image: '',
    status: 'draft' as 'published' | 'draft'
  });

  useEffect(() => {
    // 检查登录状态
    const loggedIn = localStorage.getItem('adminLoggedIn');
    const loginTime = localStorage.getItem('adminLoginTime');
    
    if (loggedIn === 'true' && loginTime) {
      const loginDate = new Date(loginTime);
      const now = new Date();
      const diffHours = (now.getTime() - loginDate.getTime()) / (1000 * 60 * 60);
      
      // 登录状态保持24小时
      if (diffHours < 24) {
        setIsLoggedIn(true);
      } else {
        localStorage.removeItem('adminLoggedIn');
        localStorage.removeItem('adminLoginTime');
      }
    }

    // 初始化文章数据（2025年）
    const initialArticles: Article[] = [
      {
        id: 1,
        category: '行业动态',
        title: '2025年碳中和政策解读：企业如何抓住绿色发展机遇',
        excerpt: '随着国家碳达峰碳中和目标的深入推进，新的政策法规为企业带来了前所未有的发展机遇。本文深入解析最新政策要点...',
        content: '随着国家碳达峰碳中和目标的深入推进，2025年新的政策法规为企业带来了前所未有的发展机遇。政府出台了一系列支持政策，包括绿色金融、税收优惠、技术补贴等措施，为企业绿色转型提供强有力的支持。\n\n企业应该如何抓住这些机遇？首先，要深入理解政策内容，制定符合自身发展的绿色战略；其次，要积极投入清洁技术研发，提升企业核心竞争力；最后，要建立完善的碳管理体系，实现精准减排。',
        date: '2025-01-15',
        readTime: '5分钟阅读',
        image: 'https://readdy.ai/api/search-image?query=Professional%20business%20meeting%20discussing%20carbon%20neutrality%20policies%20with%20charts%20and%20graphs%20about%20green%20development%20opportunities%2C%20corporate%20executives%20analyzing%20environmental%20regulations%2C%20modern%20conference%20room%20with%20sustainability%20presentations&width=400&height=300&seq=news-001&orientation=landscape',
        tags: ['政策解读', '碳中和', '绿色发展'],
        status: 'published'
      },
      {
        id: 2,
        category: '技术创新',
        title: '区块链技术在碳资产管理中的创新应用实践',
        excerpt: '探索区块链技术如何革命性地改变碳资产管理模式，提升交易透明度和可信度，为碳市场发展注入新动力...',
        content: '区块链技术在碳资产管理领域的应用正在迎来突破性进展。通过去中心化的特性，区块链能够确保碳资产交易的透明性和不可篡改性，大大提升了市场信任度。\n\n具体应用包括：碳信用的数字化确权、智能合约自动执行交易、供应链碳足迹追踪等。这些创新应用不仅提高了交易效率，还降低了管理成本，为碳市场的健康发展奠定了技术基础。',
        date: '2025-01-12',
        readTime: '7分钟阅读',
        image: 'https://readdy.ai/api/search-image?query=Blockchain%20technology%20applied%20to%20carbon%20asset%20management%20with%20digital%20networks%2C%20cryptocurrency%20interfaces%20showing%20carbon%20credits%2C%20secure%20trading%20platforms%2C%20futuristic%20fintech%20environment%20with%20green%20technology%20elements&width=400&height=300&seq=news-002&orientation=landscape',
        tags: ['区块链', '碳资产', '技术创新'],
        status: 'published'
      }
    ];
    setArticles(initialArticles);
  }, []);

  const handleLogin = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminLoginTime');
    setIsLoggedIn(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newArticle: Article = {
      id: editingArticle ? editingArticle.id : Date.now(),
      category: formData.category,
      title: formData.title,
      excerpt: formData.excerpt,
      content: formData.content,
      date: new Date().toISOString().split('T')[0],
      readTime: `${Math.ceil(formData.content.length / 200)}分钟阅读`,
      image: formData.image || `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28formData.title%29%7D&width=400&height=300&seq=article-${Date.now()}&orientation=landscape`,
      tags: formData.tags.split(',').map(tag => tag.trim()),
      status: formData.status
    };

    if (editingArticle) {
      setArticles(prev => prev.map(article => 
        article.id === editingArticle.id ? newArticle : article
      ));
    } else {
      setArticles(prev => [...prev, newArticle]);
    }

    // 重置表单
    setFormData({
      category: '',
      title: '',
      excerpt: '',
      content: '',
      tags: '',
      image: '',
      status: 'draft'
    });
    setShowAddForm(false);
    setEditingArticle(null);
  };

  const handleEdit = (article: Article) => {
    setEditingArticle(article);
    setFormData({
      category: article.category,
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      tags: article.tags.join(', '),
      image: article.image,
      status: article.status
    });
    setShowAddForm(true);
  };

  const handleDelete = (id: number) => {
    if (confirm('确定要删除这篇文章吗？')) {
      setArticles(prev => prev.filter(article => article.id !== id));
    }
  };

  const toggleStatus = (id: number) => {
    setArticles(prev => prev.map(article => 
      article.id === id 
        ? { ...article, status: article.status === 'published' ? 'draft' : 'published' }
        : article
    ));
  };

  if (!isLoggedIn) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img 
                src="https://static.readdy.ai/image/4cb3bbfdb325fa80a0fd4eacd66b0166/dc2cd215644d34678f8c19d9a3350964.png" 
                alt="捷碳科技" 
                className="h-8 w-auto"
              />
              <h1 className="text-xl font-semibold text-gray-900">管理后台</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">欢迎，管理员</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <i className="ri-logout-circle-line mr-2"></i>
                退出登录
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'dashboard'
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="ri-dashboard-line mr-2"></i>
                数据看板
              </button>
              <button
                onClick={() => setActiveTab('articles')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'articles'
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className="ri-article-line mr-2"></i>
                资讯管理
              </button>
            </nav>
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && <Dashboard />}

        {/* Articles Tab */}
        {activeTab === 'articles' && (
          <div className="space-y-8">
            {/* Action Bar */}
            <div className="flex justify-between items-center bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center space-x-4">
                <span className="text-gray-700">共 {articles.length} 篇文章</span>
                <span className="text-emerald-600">已发布 {articles.filter(a => a.status === 'published').length} 篇</span>
                <span className="text-orange-600">草稿 {articles.filter(a => a.status === 'draft').length} 篇</span>
              </div>
              <Button 
                variant="primary" 
                onClick={() => {
                  setShowAddForm(true);
                  setEditingArticle(null);
                  setFormData({
                    category: '',
                    title: '',
                    excerpt: '',
                    content: '',
                    tags: '',
                    image: '',
                    status: 'draft'
                  });
                }}
              >
                <i className="ri-add-line mr-2"></i>
                添加文章
              </Button>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold mb-6">
                  {editingArticle ? '编辑文章' : '添加新文章'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        分类
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="">选择分类</option>
                        <option value="行业动态">行业动态</option>
                        <option value="技术创新">技术创新</option>
                        <option value="案例分享">案例分享</option>
                        <option value="市场趋势">市场趋势</option>
                        <option value="解决方案">解决方案</option>
                        <option value="公司动态">公司动态</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        状态
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="draft">草稿</option>
                        <option value="published">已发布</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      文章标题
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="输入文章标题"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      文章摘要
                    </label>
                    <textarea
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      required
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="输入文章摘要（建议100-200字）"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      文章内容
                    </label>
                    <textarea
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      required
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="输入文章正文内容"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      标签
                    </label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="输入标签，用逗号分隔"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      配图链接（可选）
                    </label>
                    <input
                      type="url"
                      name="image"
                      value={formData.image}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="输入图片链接，留空将自动生成"
                    />
                  </div>

                  <div className="flex justify-end space-x-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingArticle(null);
                      }}
                    >
                      取消
                    </Button>
                    <Button type="submit" variant="primary">
                      {editingArticle ? '更新文章' : '保存文章'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Articles List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">文章列表</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {articles.map((article) => (
                  <div key={article.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium mr-3 ${
                            article.status === 'published' 
                              ? 'bg-emerald-100 text-emerald-600' 
                              : 'bg-orange-100 text-orange-600'
                          }`}>
                            {article.status === 'published' ? '已发布' : '草稿'}
                          </span>
                          <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded text-xs font-medium mr-3">
                            {article.category}
                          </span>
                          <span className="text-gray-500 text-sm">{article.date}</span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          {article.title}
                        </h4>
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center text-sm text-gray-500">
                          <span className="mr-4">{article.readTime}</span>
                          <div className="flex gap-2">
                            {article.tags.map((tag, index) => (
                              <span key={index} className="bg-gray-100 px-2 py-1 rounded text-xs">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => toggleStatus(article.id)}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            article.status === 'published'
                              ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                              : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                          }`}
                        >
                          {article.status === 'published' ? '下线' : '发布'}
                        </button>
                        <button
                          onClick={() => handleEdit(article)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
